// Resumen de la Cotización: hoy en efectivo, a crédito y lo que gana con crédito, con sus desgloses.
import { useState, type ReactNode } from 'react';
import { CircleHelp } from 'lucide-react';
import { Button, Modal } from '@/kit';
import { cn } from '@/lib/cn';
import { formatMXN } from '@/shared/lib/format/money';

const mxn = (cents: number): string => formatMXN(cents / 100);

const ROW_LABEL = 'flex items-center gap-1 text-[13px] font-semibold uppercase tracking-[0.06em]';
const BIG = 'text-[22px] font-bold tabular-nums';

export interface QuoteBigSplitProps {
    /** Enganche del crédito. */
    downPaymentCents: number;
    /** Extras que no entran al crédito; se cobran con el enganche. */
    extrasCashCents: number;
    financedCents: number;
    /** Comisión de apertura con su %; `null` si el plan no la cobra. */
    openingFee: { pct: number; cents: number } | null;
    interestCents: number;
    ivaCents: number;
    /** Suma de las cuotas, sin el enganche. */
    installmentsCents: number;
    weeklyCents: number;
    termWeeks: number;
    /** Bono de la promo de crédito que dio el servidor. */
    bonusCents: number | undefined;
}

function HelpButton({
    label,
    onClick,
    className,
}: {
    label: string;
    onClick: () => void;
    className: string;
}) {
    return (
        <button
            type="button"
            aria-label={label}
            title={label}
            onClick={onClick}
            className={cn(
                'rounded-full p-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                className,
            )}
        >
            <CircleHelp className="size-3.5" aria-hidden />
        </button>
    );
}

function Row({ label, value, strong = false }: { label: string; value: string; strong?: boolean }) {
    return (
        <div
            className={cn(
                'flex justify-between gap-3 py-1.5 text-[13px]',
                strong && 'mt-1 border-t border-border pt-2 font-semibold',
            )}
        >
            <span>{label}</span>
            <span className="tabular-nums">{value}</span>
        </div>
    );
}

function BreakdownModal({
    isOpen,
    onClose,
    title,
    children,
}: {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    children: ReactNode;
}) {
    return (
        <Modal isOpen={isOpen} onClose={onClose} title={title} size="sm">
            <div className="flex flex-col">{children}</div>
            <div className="flex justify-end pt-3">
                <Button type="button" size="sm" onClick={onClose}>
                    Entendido
                </Button>
            </div>
        </Modal>
    );
}

export function QuoteBigSplit({
    downPaymentCents,
    extrasCashCents,
    financedCents,
    openingFee,
    interestCents,
    ivaCents,
    installmentsCents,
    weeklyCents,
    termWeeks,
    bonusCents,
}: QuoteBigSplitProps) {
    const [creditOpen, setCreditOpen] = useState(false);
    const [cashOpen, setCashOpen] = useState(false);
    const cashTodayCents = downPaymentCents + extrasCashCents;
    return (
        <section
            aria-label="Resumen de la cotización"
            data-testid="quote-big-split"
            className="flex flex-col divide-y divide-border rounded-md border border-border bg-card"
        >
            <div className="flex items-baseline justify-between gap-3 p-4">
                <span className={cn(ROW_LABEL, 'text-muted-foreground')}>
                    Hoy en efectivo
                    {extrasCashCents > 0 && (
                        <HelpButton
                            label="Desglose de hoy en efectivo"
                            onClick={() => {
                                setCashOpen(true);
                            }}
                            className="text-muted-foreground/70 hover:bg-muted hover:text-foreground"
                        />
                    )}
                </span>
                <span data-testid="quote-cash-today" className={BIG}>
                    {mxn(cashTodayCents)}
                </span>
            </div>
            <div className="flex flex-col gap-1 p-4">
                <div className="flex items-baseline justify-between gap-3">
                    <span className={cn(ROW_LABEL, 'text-promo')}>
                        A crédito
                        <HelpButton
                            label="Desglose del crédito"
                            onClick={() => {
                                setCreditOpen(true);
                            }}
                            className="text-promo/70 hover:bg-promo/10 hover:text-promo"
                        />
                    </span>
                    <span className="flex items-baseline gap-2">
                        <span className="text-[11px] text-promo/80">cada semana</span>
                        <span data-testid="quote-weekly" className={cn(BIG, 'text-promo')}>
                            {mxn(weeklyCents)}
                        </span>
                    </span>
                </div>
                <p className="text-right text-[12px] text-muted-foreground tabular-nums">
                    {termWeeks} semanas · total{' '}
                    <span className="font-semibold text-foreground">{mxn(installmentsCents)}</span>
                </p>
            </div>
            {bonusCents !== undefined && bonusCents > 0 && (
                <div
                    data-testid="quote-credit-bonus"
                    className="flex items-baseline justify-between gap-3 bg-success/5 p-4"
                >
                    <span className="flex min-w-0 flex-col">
                        <span className={cn(ROW_LABEL, 'text-success')}>Gana con crédito</span>
                        <span className="text-[11.5px] text-muted-foreground">
                            Bono en PagoConFiado
                        </span>
                    </span>
                    <span className={cn(BIG, 'text-success')}>+ {mxn(bonusCents)}</span>
                </div>
            )}

            <BreakdownModal
                isOpen={cashOpen}
                onClose={() => {
                    setCashOpen(false);
                }}
                title="Hoy en efectivo"
            >
                <Row label="Enganche del crédito" value={mxn(downPaymentCents)} />
                <Row label="Contado de extras" value={mxn(extrasCashCents)} />
                <Row label="Total hoy" value={mxn(cashTodayCents)} strong />
                <p className="mt-3 text-[11.5px] text-muted-foreground">
                    El enganche va al crédito como pago inicial. El contado cubre los extras que no
                    entran al crédito.
                </p>
            </BreakdownModal>

            <BreakdownModal
                isOpen={creditOpen}
                onClose={() => {
                    setCreditOpen(false);
                }}
                title="Desglose del crédito"
            >
                <Row label="Monto financiado (principal)" value={mxn(financedCents)} />
                {openingFee !== null && (
                    <Row
                        label={`Comisión de apertura (${String(openingFee.pct)}%)`}
                        value={mxn(openingFee.cents)}
                    />
                )}
                <Row label="Intereses" value={mxn(interestCents)} />
                <Row label="IVA sobre intereses (16%)" value={mxn(ivaCents)} />
                <Row label="Total en cuotas" value={mxn(installmentsCents)} strong />
                <p className="mt-3 text-[11.5px] text-muted-foreground">
                    {termWeeks} cuotas fijas de{' '}
                    <span className="font-semibold tabular-nums text-foreground">
                        {mxn(weeklyCents)}
                    </span>{' '}
                    (IVA incluido en cada cuota).
                </p>
            </BreakdownModal>
        </section>
    );
}
